import { getAllPurchaseOrders } from './purchaseOrdersService';
import { fetchCableDeliveriesStateMap } from './cableDeliveriesService';
import { correctPoItemName } from '../utils/poItemNameNormalize';

const DELIVERY_TABLES_STORAGE_KEY = 'deliveryTracking:cableTablesState:v1';

const normalizeItemName = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const parseNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const num = parseFloat(String(value).replace(/,/g, '').trim());
  return Number.isNaN(num) ? 0 : num;
};

const sumDeliveredForRow = (row = {}) =>
  Object.values(row).reduce((sum, value) => sum + parseNumber(value), 0);

const normalizePoItems = (itemsValue) => {
  if (Array.isArray(itemsValue)) return itemsValue;
  if (typeof itemsValue === 'string') {
    try {
      const parsed = JSON.parse(itemsValue);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      return [];
    }
  }
  return [];
};

const toCategoryLabel = (po) => {
  const source = String(po?.subcategory || po?.category || '').trim();
  if (!source) return 'General';
  return source
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCorrectedMaterialName = (lot, itemName) => correctPoItemName(lot, itemName);

const isTransformerPoOrItem = (po, itemName = '') => {
  const categoryLabel = toCategoryLabel(po);
  if (categoryLabel.toLowerCase().includes('transformer')) return true;
  return String(itemName || '').toLowerCase().includes('transformer');
};

/** Normalized inventory key used to match delivery-table rows to inventory rows. */
const getDeliveryTrackingKey = (po, rawItemName) =>
  normalizeItemName(getCorrectedMaterialName(po.lot, rawItemName));

/** Display name for PO line items in inventory and delivery tables. */
export function getInventoryMaterialDisplayName(po, rawItemName) {
  return getCorrectedMaterialName(po?.lot, rawItemName);
}

const usesDeliveryTableTotals = (category = '') => {
  const label = String(category || '').toLowerCase();
  return label.includes('cable') || label.includes('transformer');
};

/**
 * Load the same flattened material rows that the Material Inventory page shows,
 * including live "delivered" quantities sourced from Appwrite + localStorage.
 *
 * Returns an array of rows shaped like:
 *  { lot, category, materialName, boqQty, qtyDelivered, deliveredPercent,
 *    boqBalanceQty, boqBalancePercent, sourcePoId, sourceItemIndex }
 */
export async function loadMaterialInventoryRows() {
  const purchaseOrders = await getAllPurchaseOrders();
  const dedupedRows = new Map();
  let runningCode = 1;

  (purchaseOrders || [])
    .filter((po) => String(po?.category || '').toLowerCase() === 'material')
    .forEach((po) => {
      const items = normalizePoItems(po.items);
      items.forEach((item, itemIndex) => {
        const rawItemName = String(
          item?.itemName || item?.name || item?.item || item?.description || ''
        ).trim();
        if (!rawItemName) return;

        const itemName = getCorrectedMaterialName(po.lot, rawItemName);
        const boqQty = parseNumber(item?.qty ?? item?.quantity ?? item?.poQty ?? item?.po_qty);
        const lot = po.lot || 'Unassigned';
        const normalizedName = normalizeItemName(itemName);
        const categoryLabel = toCategoryLabel(po);
        const isTransformer = isTransformerPoOrItem(po, itemName);
        const displayItemName = itemName;
        const dedupeKey = `${lot}::${normalizedName}`;
        const category = isTransformer ? 'Transformers' : categoryLabel;

        if (dedupedRows.has(dedupeKey)) {
          const existing = dedupedRows.get(dedupeKey);
          const mergedBoqQty = (existing.boqQty || 0) + (boqQty > 0 ? boqQty : 0);
          dedupedRows.set(dedupeKey, {
            ...existing,
            boqQty: mergedBoqQty,
            boqBalanceQty: Math.max(0, mergedBoqQty - (existing.qtyDelivered || 0))
          });
          return;
        }

        dedupedRows.set(dedupeKey, {
          id: `${po.id}-${itemIndex}`,
          materialCode: `MAT-${String(runningCode++).padStart(4, '0')}`,
          sourcePoId: po.id,
          sourceItemIndex: itemIndex,
          lot,
          materialName: displayItemName,
          category,
          boqQty: boqQty > 0 ? boqQty : 0,
          qtyDelivered: 0,
          deliveredPercent: 0,
          boqBalancePercent: 0,
          boqBalanceQty: boqQty > 0 ? boqQty : 0
        });
      });
    });

  let remoteState = {};
  try {
    remoteState = await fetchCableDeliveriesStateMap();
  } catch (err) {
    console.warn('Cable deliveries from Appwrite unavailable, using local/cache only:', err);
  }

  let localState = {};
  try {
    const rawState = localStorage.getItem(DELIVERY_TABLES_STORAGE_KEY);
    if (rawState) localState = JSON.parse(rawState) || {};
  } catch (_) {
    localState = {};
  }

  const tableStateByPo = { ...localState, ...remoteState };
  const poById = new Map((purchaseOrders || []).map((po) => [po.id, po]));
  const deliveredByItemName = {};
  const deliveredByLotAndItemName = {};

  if (tableStateByPo && typeof tableStateByPo === 'object') {
    Object.entries(tableStateByPo).forEach(([poId, poState]) => {
      const po = poById.get(poId);
      if (!po) return;
      const items = normalizePoItems(po.items);
      const deliveryData = poState?.deliveryData || {};
      items.forEach((item, rowIndex) => {
        const rowDelivered = sumDeliveredForRow(deliveryData[rowIndex] || {});
        if (rowDelivered <= 0) return;
        const rawItemName =
          item?.itemName || item?.name || item?.item || item?.description || '';
        const itemName = getDeliveryTrackingKey(po, rawItemName);
        if (!itemName) return;
        const lotKey = normalizeItemName(po.lot || 'Unassigned');

        deliveredByItemName[itemName] = (deliveredByItemName[itemName] || 0) + rowDelivered;
        const lotItemKey = `${lotKey}::${itemName}`;
        deliveredByLotAndItemName[lotItemKey] =
          (deliveredByLotAndItemName[lotItemKey] || 0) + rowDelivered;
      });
    });
  }

  return Array.from(dedupedRows.values()).map((material) => {
    const materialKey = normalizeItemName(material.materialName);
    const lotKey = normalizeItemName(material.lot || 'Unassigned');
    const lotItemKey = `${lotKey}::${materialKey}`;
    const fromDeliveryTables = usesDeliveryTableTotals(material.category);

    let deliveredFromTracking = deliveredByLotAndItemName[lotItemKey];
    if (deliveredFromTracking === undefined) {
      deliveredFromTracking = deliveredByItemName[materialKey];
    }
    if (deliveredFromTracking === undefined) {
      const fuzzyMatchKey = Object.keys(deliveredByItemName).find(
        (key) => key.includes(materialKey) || materialKey.includes(key)
      );
      if (fuzzyMatchKey) deliveredFromTracking = deliveredByItemName[fuzzyMatchKey];
    }

    const exactDeliveryRow =
      tableStateByPo?.[material.sourcePoId]?.deliveryData?.[material.sourceItemIndex];
    const exactDeliveredQty = exactDeliveryRow ? sumDeliveredForRow(exactDeliveryRow) : undefined;

    const qtyDelivered = fromDeliveryTables
      ? (
          !String(material.category || '').toLowerCase().includes('transformer') &&
          exactDeliveredQty !== undefined
            ? exactDeliveredQty
            : deliveredFromTracking !== undefined
              ? deliveredFromTracking
              : 0
        )
      : deliveredFromTracking !== undefined
        ? deliveredFromTracking
        : material.qtyDelivered;

    const deliveredPercent = material.boqQty > 0 ? (qtyDelivered / material.boqQty) * 100 : 0;
    const boqBalanceQty = Math.max(0, material.boqQty - qtyDelivered);
    const boqBalancePercent = material.boqQty > 0 ? (boqBalanceQty / material.boqQty) * 100 : 0;

    return {
      ...material,
      qtyDelivered,
      deliveredPercent,
      boqBalanceQty,
      boqBalancePercent
    };
  });
}

const LOT8_TAGS = ['lot 8', 'lot8'];
const LOT10_TAGS = ['lot 10', 'lot10'];

const matchesLot = (lot, tags) => {
  const s = String(lot || '').toLowerCase();
  return tags.some((t) => s.includes(t));
};

/**
 * Group material rows by LOT (LOT 8 / LOT 10) and then by category, returning per-bucket
 * totals so the dashboard inventory summary can render them.
 */
export function summarizeMaterialRowsByLot(rows) {
  const emptyBucket = () => ({
    items: 0,
    boqQty: 0,
    qtyDelivered: 0,
    boqBalanceQty: 0
  });

  const makeContainer = () => ({
    categories: new Map(),
    totals: emptyBucket()
  });

  const lot8 = makeContainer();
  const lot10 = makeContainer();

  const pushTo = (container, material) => {
    const cat = material.category || 'General';
    const bucket = container.categories.get(cat) || emptyBucket();
    bucket.items += 1;
    bucket.boqQty += Number(material.boqQty) || 0;
    bucket.qtyDelivered += Number(material.qtyDelivered) || 0;
    bucket.boqBalanceQty += Number(material.boqBalanceQty) || 0;
    container.categories.set(cat, bucket);

    container.totals.items += 1;
    container.totals.boqQty += Number(material.boqQty) || 0;
    container.totals.qtyDelivered += Number(material.qtyDelivered) || 0;
    container.totals.boqBalanceQty += Number(material.boqBalanceQty) || 0;
  };

  rows.forEach((m) => {
    if (matchesLot(m.lot, LOT8_TAGS)) pushTo(lot8, m);
    else if (matchesLot(m.lot, LOT10_TAGS)) pushTo(lot10, m);
  });

  const flatten = (container) => ({
    totals: container.totals,
    byCategory: Array.from(container.categories.entries()).map(([category, bucket]) => ({
      category,
      ...bucket,
      deliveredPercent: bucket.boqQty > 0 ? Math.min(100, (bucket.qtyDelivered / bucket.boqQty) * 100) : 0
    }))
  });

  return {
    lot8: flatten(lot8),
    lot10: flatten(lot10)
  };
}
